/**
 * @file agencyName.js
 * @description Agency Display Name (DBA) helpers.
 *
 *   Platform agencies have TWO names:
 *     • agency_name — the NPI-locked LEGAL entity name (e.g. "THE ABACORE
 *       THERAPY LLC"). Used for legal/financial/compliance and NPI/fraud matching.
 *     • dba_name   — the "Display Name" / Doing Business As, shown to therapists
 *       everywhere in the marketplace (e.g. "The ABACore Therapy").
 *
 *   `agencyDisplayName()` is the single read helper: it prefers the DBA and falls
 *   back to the legal name so a missing DBA is never blank. `toDisplayName()`
 *   generates a clean starting Display Name from an all-caps legal/NPI name for
 *   the onboarding pre-fill (the agency can then edit it).
 * @module lib/agencyName
 */

// Tokens kept UPPERCASE when title-casing an all-caps legal name.
const ACRONYMS = new Set([
  "ABA", "LLC", "PLLC", "INC", "LLP", "LP", "PC", "PA", "LBA",
  "BCBA", "BCABA", "RBT", "DBA", "USA", "US", "II", "III",
]);
// Small words kept lowercase when not the first word.
const CONNECTORS = new Set(["of", "the", "and", "for", "to", "in", "at", "on", "a", "an", "or"]);

/**
 * Produce a clean Display Name from a legal/NPI organization name. If the input
 * already has mixed case (looks human-entered, e.g. "Bright Path ABA"), it is
 * returned as-is. An all-caps registry name is title-cased with common ABA/legal
 * acronyms preserved and connector words lowercased. This is only a starting
 * suggestion — the agency confirms/edits it. Never throws.
 * @param {string} legal
 * @returns {string}
 */
export function toDisplayName(legal) {
  const raw = (legal || "").trim();
  if (!raw) return "";
  // Already mixed-case → assume it's already a usable display name.
  if (/[a-z]/.test(raw)) return raw;
  return raw
    .toLowerCase()
    .split(/\s+/)
    .map((word, i) => {
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      if (i > 0 && CONNECTORS.has(word)) return word;
      // Capitalize the first letter of each hyphen-separated part.
      return word.replace(/(^|[-/])([a-z])/g, (_, sep, c) => sep + c.toUpperCase());
    })
    .join(" ");
}

/**
 * The name to SHOW a therapist for an agency or case. Prefers the DBA, falls
 * back to the legal name, then a generic label — so it is never blank. Accepts
 * either DB-row shapes (dba_name / agency_name) or mapped objects (dbaName /
 * agency). Never throws.
 * @param {Object} obj
 * @returns {string}
 */
export function agencyDisplayName(obj) {
  if (!obj) return "Agency";
  return (
    obj.dba_name || obj.dbaName ||
    obj.agency_name || obj.agencyName || obj.agency ||
    "Agency"
  );
}
